import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FaCogs } from 'react-icons/fa';
import { yupResolver } from '@hookform/resolvers/yup';
import { MdGroupWork } from 'react-icons/md';
import { PropagateLoader } from 'react-spinners';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

import AddButton from '~/components/Buttons/AddAlt';
import BreadCrumb from '~/components/BreadCrumb';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import DefaultInput from '~/components/Inputs/Default';
import DeleteButton from '~/components/Buttons/DeletePrevious';
import DialogOptionButton from '~/components/Buttons/DialogOption';
import ExitButton from '~/components/Buttons/Exit';
import SearchButton from '~/components/Buttons/Search';
import SelectAltButton from '~/components/Buttons/SelectAlt';
import Pagination from '~/components/Pagination';
import SaveButton from '~/components/Buttons/Save';

import { getFormattedCnpj } from '~/utils';

import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, Table, Td, StyledModal, Error } from './styles';

interface IFormPolicyGroup {
  name: string;
}

interface IClient {
  new: boolean;
  code: string;
  name: string;
  document: string;
  city: string;
  state: string;
}

interface IClientOption {
  code: string;
  company_name: string;
  document: string;
  city: string;
  state: string;
}

const schema = Yup.object().shape({
  name: Yup.string().required('Campo obrigatório'),
});

const OP_AMOUNT_PER_PAGE = 5;

const Record: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { state } = useLocation<{ id: string; name: string }>();
  const [clients, setClients] = useState<IClient[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientOptions, setClientOptions] = useState<{
    isOpen: boolean;
    loading: boolean;
    page: number;
    filter: string;
    selectedCode: string;
    isValidating: boolean;
    error: string;
    options: { clients: IClientOption[]; count: number };
  }>({
    isOpen: false,
    loading: false,
    page: 0,
    filter: '',
    selectedCode: '',
    isValidating: false,
    error: '',
    options: { clients: [], count: 0 },
  });
  const [optionsPage, setOptionsPage] = useState(0);
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, register, errors } = useForm<IFormPolicyGroup>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const loadClients = async () => {
      await api.get(`/policygroups/${state.id}`).then(response => {
        setClients(
          response.data.policy_group_clients.map(
            (group_client: {
              local_client: {
                code: string;
                company_name: string;
                document: string;
                city: string;
                state: string;
              };
            }) => ({
              new: false,
              code: group_client.local_client.code,
              name: group_client.local_client.company_name,
              document: group_client.local_client.document,
              city: group_client.local_client.city,
              state: group_client.local_client.state,
            }),
          ),
        );

        setLoadingClients(false);
      });
    };

    if (state === undefined) {
      setLoadingClients(false);
    } else {
      loadClients();
    }
  }, [state]);

  useEffect(() => {
    const loadClientOptions = async (op_page: number) => {
      await api
        .get(
          `/clients/paginate?` +
            `page=${clientOptions.page}&` +
            `filter=${clientOptions.filter}&` +
            `user_type=&` +
            `erp_rep_code=&` +
            `amount=${OP_AMOUNT_PER_PAGE}`,
        )
        .then(response => {
          setClientOptions(prev => ({
            ...prev,
            loading: false,
            page: op_page,
            options: {
              clients: response.data[0],
              count: response.data[1],
            },
          }));
        });
    };

    if (clientOptions.loading) {
      loadClientOptions(clientOptions.page);
    } else if (optionsPage !== clientOptions.page) {
      loadClientOptions(optionsPage);
    }
  }, [
    clientOptions.filter,
    clientOptions.loading,
    clientOptions.page,
    optionsPage,
  ]);

  useEffect(() => {
    const validateClient = async () => {
      try {
        await api
          .get(`/policygroupclients/validate/${clientOptions.selectedCode}`)
          .then(response => {
            let available = false;

            if (response.data.status === 'available') {
              available =
                clients.filter(cl => cl.code === clientOptions.selectedCode)
                  .length === 0;
            }

            if (available) {
              const findNewClient = clientOptions.options.clients.find(
                cl => cl.code === clientOptions.selectedCode,
              );

              if (findNewClient) {
                setClients(prevClients => [
                  ...prevClients,
                  {
                    new: true,
                    code: findNewClient.code,
                    name: findNewClient.company_name,
                    document: findNewClient.document,
                    city: findNewClient.city,
                    state: findNewClient.state,
                  },
                ]);

                setClientOptions(prev => ({
                  ...prev,
                  isValidating: false,
                  isOpen: false,
                }));
              } else {
                available = false;
              }
            }

            if (!available) {
              setClientOptions(prev => ({
                ...prev,
                isValidating: false,
                error: 'Cliente indisponível',
              }));
            }
          });
      } catch (error: any) {
        setClientOptions(prev => ({
          ...prev,
          isValidating: false,
          error: error.response.data.message,
        }));
      }
    };

    if (clientOptions.isValidating) {
      validateClient();
    }
  }, [
    clientOptions.isValidating,
    clientOptions.options.clients,
    clientOptions.selectedCode,
    clients,
  ]);

  const submitForm = useCallback(
    async (data: IFormPolicyGroup) => {
      try {
        setIsSaving(true);

        if (state === undefined) {
          await api.post('/policygroups', { ...data, clients });

          addToast({
            type: 'success',
            title: 'Cadastro realizado!',
            description: 'Grupo de política comercial cadastrado com sucesso.',
          });
        } else {
          await api.put(`/policygroups`, {
            ...data,
            id: state.id,
            clients,
          });

          addToast({
            type: 'success',
            title: 'Cadastro atualizado!',
            description: 'Grupo de política comercial atualizado com sucesso.',
          });
        }

        history.push('/configurations/policygroups');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, clients, history, state],
  );

  return (
    <Container>
      <BreadCrumb
        main="Configurações"
        path="/configurations/main"
        firstChildPath="/configurations/policygroups"
        firstChild="Grupos de Políticas Comerciais"
        secondChild={state === undefined ? 'Novo Grupo' : 'Edição de Grupo'}
        mainIcon={FaCogs}
        firstChildIcon={MdGroupWork}
        secondChildIcon={MdGroupWork}
        mainColored
        firstChildColored
      />
      <form onSubmit={handleSubmit(submitForm)}>
        <div style={{ width: 400 }}>
          <DefaultInput
            name="name"
            labelFor="name"
            labelText="Nome"
            type="text"
            defaultValue={state?.name}
            error={errors?.name?.message}
            ref={register}
            maxLength={60}
          />
        </div>

        <div
          style={{ marginTop: 30, display: 'flex', justifyContent: 'center' }}
        >
          <Table>
            <thead>
              <tr>
                <th colSpan={4} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontStyle: 'italic' }}>
                    Clientes
                  </div>
                </th>
              </tr>
              <tr>
                <th style={{ width: '15%', textAlign: 'left' }}>Código</th>
                <th style={{ width: '30%', textAlign: 'left' }}>Nome</th>
                <th style={{ width: '15%', textAlign: 'left' }}>CNPJ</th>
                <th style={{ width: '20%', textAlign: 'left' }}>Cidade</th>
                <th style={{ width: '10%', textAlign: 'left' }}>Estado</th>
                <th style={{ width: '10%', textAlign: 'center' }} />
              </tr>
            </thead>

            <tbody>
              {loadingClients ? (
                <tr>
                  <Td
                    colSpan={4}
                    style={{ padding: '3rem', textAlign: 'center' }}
                  >
                    <PropagateLoader color="#276c8d" />
                  </Td>
                </tr>
              ) : clients.length > 0 ? (
                clients.map(client => (
                  <tr key={`${client.code}-${uuidv4()}`}>
                    <Td
                      style={{ width: '15%', textAlign: 'left' }}
                      heading="CÓDIGO"
                    >
                      {client.code}
                    </Td>

                    <Td
                      style={{ width: '30%', textAlign: 'left' }}
                      heading="NOME"
                    >
                      {client.name}
                    </Td>

                    <Td
                      style={{ width: '15%', textAlign: 'left' }}
                      heading="CNPJ"
                    >
                      {getFormattedCnpj(client.document)}
                    </Td>

                    <Td
                      style={{ width: '20%', textAlign: 'left' }}
                      heading="CIDADE"
                    >
                      {client.city}
                    </Td>

                    <Td
                      style={{ width: '10%', textAlign: 'left' }}
                      heading="ESTADO"
                    >
                      {client.state}
                    </Td>

                    <Td
                      style={{ width: '10%', textAlign: 'center' }}
                      heading=""
                    >
                      {client.new && (
                        <DeleteButton
                          type="button"
                          onClick={() => {
                            setClients(
                              clients.filter(cl => cl.code !== client.code),
                            );
                          }}
                        />
                      )}
                    </Td>
                  </tr>
                ))
              ) : (
                <tr>
                  <Td colSpan={4} style={{ textAlign: 'center' }}>
                    Nenhum cliente vinculado à este grupo
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div
          style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}
        >
          <AddButton
            onClick={() => {
              setClientOptions(prev => ({
                ...prev,
                isOpen: true,
                loading: true,
                page: 0,
                filter: '',
                selectedCode: '',
                isValidating: false,
                error: '',
                options: { clients: [], count: 0 },
              }));
            }}
          />
        </div>

        <div
          style={{
            marginTop: 50,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            justifyContent: 'flex-end',
          }}
        >
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              setIsOpenConfirmProcedure(true);
            }}
          />

          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </div>
      </form>

      <StyledModal
        isOpen={clientOptions.isOpen}
        style={{
          overlay: {
            zIndex: 999,
            overflowY: 'auto',
            position: 'fixed',
            background: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <div style={{ padding: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              fontSize: 20,
              fontStyle: 'italic',
              fontWeight: 'bold',
            }}
          >
            Seleção de Clientes
          </div>

          <div
            style={{
              marginTop: 20,
              display: 'flex',
              alignItems: 'flex-end',
              columnGap: 30,
            }}
          >
            <div style={{ maxWidth: 400, width: '100%' }}>
              <DefaultInput
                name="search"
                labelText="Pesquisar"
                type="text"
                disabled={clientOptions.loading}
                onChange={event => {
                  setClientOptions(prev => ({
                    ...prev,
                    filter: event.target.value,
                    selectedCode: '',
                    isValidating: false,
                    error: '',
                  }));
                }}
              />
            </div>

            <SearchButton
              disabled={clientOptions.loading}
              onClick={() => {
                setClientOptions(prev => ({
                  ...prev,
                  loading: true,
                  page: 0,
                  options: { clients: [], count: 0 },
                }));
              }}
            />
          </div>

          <div>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '15%', textAlign: 'left' }}>Código</th>
                  <th style={{ width: '40%', textAlign: 'left' }}>Nome</th>
                  <th style={{ width: '20%', textAlign: 'left' }}>CNPJ</th>
                  <th style={{ width: '25%', textAlign: 'center' }} />
                </tr>
              </thead>

              <tbody>
                {clientOptions.loading ? (
                  <tr>
                    <Td
                      colSpan={6}
                      style={{ padding: '3rem', textAlign: 'center' }}
                    >
                      <PropagateLoader color="#276c8d" />
                    </Td>
                  </tr>
                ) : clientOptions.options.clients.length > 0 ? (
                  clientOptions.options.clients.map(client => (
                    <tr key={`${client.code}-${uuidv4()}`}>
                      <Td
                        style={{ width: '15%', textAlign: 'left' }}
                        heading="CÓDIGO"
                      >
                        {client.code}
                      </Td>

                      <Td
                        style={{ width: '40%', textAlign: 'left' }}
                        heading="NOME"
                      >
                        {client.company_name}
                      </Td>

                      <Td
                        style={{ width: '20%', textAlign: 'left' }}
                        heading="CNPJ"
                      >
                        {getFormattedCnpj(client.document)}
                      </Td>

                      <Td
                        style={{ width: '25%', textAlign: 'center' }}
                        heading=""
                      >
                        {(clientOptions.selectedCode === '' ||
                          client.code === clientOptions.selectedCode) && (
                          <SelectAltButton
                            selected={clientOptions.selectedCode !== ''}
                            onClick={() => {
                              setClientOptions(prev => ({
                                ...prev,
                                selectedCode:
                                  clientOptions.selectedCode === ''
                                    ? client.code
                                    : '',
                                error: '',
                              }));
                            }}
                          />
                        )}
                      </Td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <Td colSpan={6} style={{ textAlign: 'center' }}>
                      Nenhum cliente disponível
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div style={{ marginTop: 20 }}>
            <Pagination
              page={clientOptions.page}
              count={clientOptions.options.count}
              amountPerPage={OP_AMOUNT_PER_PAGE}
              setPage={setOptionsPage}
            />
          </div>

          {clientOptions.error !== '' && <Error>{clientOptions.error}</Error>}

          <div
            style={{
              margin: 20,
              display: 'flex',
              justifyContent: 'center',
              columnGap: 20,
            }}
          >
            <DialogOptionButton
              label="Confirmar"
              optionType="success"
              disabled={
                clientOptions.selectedCode === '' || clientOptions.isValidating
              }
              onClick={() => {
                setClientOptions({
                  ...clientOptions,
                  isValidating: true,
                  error: '',
                });
              }}
            />

            <DialogOptionButton
              label="Cancelar"
              optionType="danger"
              disabled={clientOptions.isValidating}
              onClick={() => {
                setClientOptions(prev => ({ ...prev, isOpen: false }));
              }}
            />
          </div>
        </div>
      </StyledModal>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => handleSubmit(submitForm)()}
        title="Confirma gravação do grupo de política comercial?"
        subTitle=""
        opConfirm="Sim"
        type=""
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={() => history.push('/configurations/policygroups')}
      />
    </Container>
  );
};

export default Record;
